import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch, store } from "../../../../redux/store";
import { fetchInvoices } from "../actions";
import { fetchCurrencies } from "../../settings/currencies/actions";
import { useNavigate } from "react-router-dom";
import MainTable from "../../../../components/mainTable";
import { Invoice } from "../../../../interfaces/screens/billing";
import {
  showModal,
  hideModal,
} from "../../../../connected-components/modal/reducer";
import InvoiceEditor from "../../../../components/invoiceEditor";
import ClaimEditor from "../../../../components/claimEditor";
import ExportInvoiceModal from "../../../../components/exportInvoiceModal";
import { generateInvoicePdf } from "../../../../shared/utils/pdf";
import logo from "../../../../assets/images/icons/logo-white.svg";
import { getInvoicesTableColumns } from "../../../../shared/tableColumns/invoicesColumns";
import { ModalType } from "../../../../shared/utils/enums";

const InvoicesDashboard = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    data: invoices,
    loading,
    error,
  } = useSelector((state: RootState) => state.billing.invoices);
  const { data: currencies } = useSelector(
    (state: RootState) => state.currencies,
  );

  useEffect(() => {
    dispatch(fetchInvoices());
    if (!currencies || currencies.length === 0) {
      dispatch(fetchCurrencies());
    }
  }, [dispatch, currencies]);

  const handleNewInvoice = () => {
    dispatch(
      showModal({
        title: "Create New Invoice",
        type: ModalType.COMPONENT,
        component: (
          <InvoiceEditor
            key="new-invoice"
            onClose={() => store.dispatch(hideModal())}
          />
        ),
      }),
    );
  };

  const handleEditInvoice = (invoice: Invoice) => {
    dispatch(
      showModal({
        title: `Edit Invoice ${invoice.invoice_number}`,
        type: ModalType.COMPONENT,
        component: (
          <InvoiceEditor
            key={invoice.id}
            invoice={invoice}
            onClose={() => store.dispatch(hideModal())}
          />
        ),
      }),
    );
  };

  const handleGenerateClaim = (invoice: Invoice) => {
    dispatch(
      showModal({
        title: `New Claim for Invoice ${invoice.invoice_number}`,
        type: ModalType.COMPONENT,
        component: (
          <ClaimEditor
            key={`new-claim-${invoice.id}`}
            invoice={invoice}
            onClose={() => store.dispatch(hideModal())}
          />
        ),
      }),
    );
  };

  const handleExportPdf = async (invoice: Invoice) => {
    try {
      const response = await fetch(logo);
      const logoSvg = await response.text();
      generateInvoicePdf(invoice, logoSvg);
    } catch (error) {
      console.error("Error loading logo for PDF:", error);
      alert("Could not generate PDF. See console for details.");
    } finally {
      dispatch(hideModal());
    }
  };

  const handleOpenExportModal = (invoice: Invoice) => {
    dispatch(
      showModal({
        title: `Export Invoice ${invoice.invoice_number}`,
        type: ModalType.COMPONENT,
        width: "40%",
        component: (
          <ExportInvoiceModal
            invoice={invoice}
            onClose={() => dispatch(hideModal())}
            onExport={() => handleExportPdf(invoice)}
          />
        ),
      }),
    );
  };

  const columns = getInvoicesTableColumns({
    onEdit: handleEditInvoice,
    onGenerateClaim: handleGenerateClaim,
    onExport: handleOpenExportModal,
  });

  return (
    <div className="pt-4">
      <MainTable
        data={invoices}
        columns={columns}
        isLoading={loading}
        showAddButton={true}
        addButtonLabel="New Invoice"
        onAddNewItemPress={handleNewInvoice}
        onRowPress={(row: Invoice) => handleEditInvoice(row)}
      />
      {error && (
        <p className="text-red-500 mt-4">Error fetching invoices: {error}</p>
      )}
    </div>
  );
};

export default InvoicesDashboard;
